/**
 * Jun-2026 (User-Wunsch) — Live-Handoff QR-Code an der Theke.
 *
 * Erzeugt eine tokenisierte Scan-Session. Mitarbeiter scannt den QR mit dem
 * Handy (KEIN Login nötig, öffentliche Seite /m/scan/:token), fotografiert
 * mehrere Belege → das Bündel wird per Polling abgeholt und sofort am Desktop
 * in den Kunden-Picker übernommen.
 *
 * variant="header": größerer, direkt scannbarer QR (oben rechts neben dem Titel).
 * Klick öffnet ein Popover mit großem QR + Druck-Funktion.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Loader2, CheckCircle2, Printer, Mail, MessageCircle, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export default function MobileZb1QrCard({ onScanned, variant = "inline", purpose = null, listingId = null }) {
    const { user } = useAuth();
    const isPapers = purpose === "papers";
    const [session, setSession] = useState(null); // { token, portal_url, branch_label }
    const [scanned, setScanned] = useState(false);
    const [sendPhone, setSendPhone] = useState("");
    const [sendEmail, setSendEmail] = useState("");
    const [sending, setSending] = useState("");
    const [custQ, setCustQ] = useState("");
    const [custResults, setCustResults] = useState([]);
    useEffect(() => {
        const term = custQ.trim();
        if (!term) { setCustResults([]); return; }
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get(`/customers?q=${encodeURIComponent(term)}&limit=8`);
                setCustResults(data || []);
            } catch { setCustResults([]); }
        }, 300);
        return () => clearTimeout(h);
    }, [custQ]);
    const pickCustomer = (c) => {
        setSendPhone(c.phone || "");
        setSendEmail(c.email || "");
        setCustQ("");
        setCustResults([]);
        toast.success(`${c.first_name || c.company_name || "Kunde"} übernommen.`);
    };
    const pollRef = useRef(null);
    const handledRef = useRef(false);
    const onScannedRef = useRef(onScanned);
    useEffect(() => { onScannedRef.current = onScanned; }, [onScanned]);
    const initRef = useRef(false);

    const createSession = useCallback(async () => {
        try {
            handledRef.current = false;
            setScanned(false);
            const payload = {};
            if (purpose) payload.purpose = purpose;
            if (listingId) payload.listing_id = listingId;
            const { data } = await api.post("/work-orders/scan-sessions", payload);
            setSession(data);
        } catch {
            setSession(null);
        }
    }, [purpose, listingId]);

    useEffect(() => { if (initRef.current) return; initRef.current = true; createSession(); }, [createSession]);

    // Polling: solange eine Session aktiv ist, auf das „Fertig"-Bündel warten.
    useEffect(() => {
        if (!session?.token) return;
        const token = session.token;
        const tick = async () => {
            try {
                const { data } = await api.get(`/work-orders/scan-sessions/${token}`);
                if (data.status === "complete" && (data.documents || []).length && !handledRef.current) {
                    handledRef.current = true;
                    setScanned(true);
                    toast.success(`${data.count} Beleg(e) vom Handy empfangen — werden übernommen`);
                    try { await api.post(`/work-orders/scan-sessions/${token}/consume`); } catch { /* noncritical */ }
                    onScannedRef.current?.({ documents: data.documents, match: data.match, order_id: data.order_id, order_number: data.order_number });
                    setTimeout(() => createSession(), 1500);
                } else if (data.status === "expired") {
                    createSession();
                }
            } catch { /* ignore transient errors */ }
        };
        pollRef.current = setInterval(tick, 3000);
        return () => clearInterval(pollRef.current);
    }, [session, createSession]);

    const scanUrl = session?.portal_url || "";

    const handlePrint = async () => {
        if (!scanUrl) return;
        const ok = await openQrPrint({
            title: "Belege per Handy scannen",
            subtitle: isPapers
                ? "Mit der Handy-Kamera den Fahrzeugschein (ZB1) fotografieren. Die Fahrzeugdaten erscheinen automatisch am PC."
                : "Mit der Handy-Kamera scannen — ZB1, Ausweis/Führerschein, Rechnung/Auftrag oder Schadensfotos aufnehmen. Die erkannten Daten erscheinen automatisch am Theken-PC.",
            scanUrl,
            ...dealerPrintInfo(user),
        });
        if (!ok) toast.error("Bitte Pop-ups erlauben, um zu drucken.");
    };

    // --- Versand des Selbstannahme-Links an den Kunden (WhatsApp/SMS/E-Mail) ----
    const waNum = (phone) => {
        let d = String(phone || "").replace(/[^\d+]/g, "");
        if (!d) return null;
        if (d.startsWith("+")) d = d.slice(1);
        else if (d.startsWith("00")) d = d.slice(2);
        else if (d.startsWith("0")) d = "49" + d.slice(1);
        return d.length >= 8 ? d : null;
    };

    const sendWhatsApp = () => {
        const num = waNum(sendPhone);
        if (!num) { toast.error("Bitte gültige Handynummer eingeben."); return; }
        if (!scanUrl) return;
        const text = `Nehmen Sie Ihr Fahrzeug bequem per Handy auf — Belege fotografieren & Auftrag starten: ${scanUrl} (Link 30 Min. gültig)`;
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank");
        toast.success("WhatsApp geöffnet — Nachricht prüfen & senden.");
    };

    const sendVia = async (channel) => {
        const to = channel === "sms" ? sendPhone.trim() : sendEmail.trim();
        if (!to) { toast.error(channel === "sms" ? "Telefonnummer fehlt." : "E-Mail fehlt."); return; }
        if (!session?.token) return;
        setSending(channel);
        try {
            const { data } = await api.post(`/work-orders/scan-sessions/${session.token}/send`, { channel, to });
            toast.success(
                (channel === "sms" ? "SMS" : "E-Mail") + " versendet" +
                (data?.mocked ? " (Demo/Mock — bis Live-Key gesetzt)" : "") + ".");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen.");
        } finally {
            setSending("");
        }
    };

    const popover = (
        <PopoverContent align="end" className="w-80" data-testid="wo-mobile-zb1-qr-popover">
            <div className="flex flex-col items-center text-center gap-2">
                <div className="font-display font-black text-sm flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-[#111827]" /> Belege am Handy scannen
                </div>
                <div className="p-2 bg-white border-2 border-[#FFCC00] rounded-sm">
                    {scanUrl
                        ? <QRCodeSVG value={scanUrl} size={180} level="M" title="QR-Code zur mobilen Auftragserfassung" data-testid="wo-mobile-zb1-qr-large" />
                        : <div className="w-[180px] h-[180px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
                </div>
                <p className="text-[11px] text-gray-600 leading-snug">
                    Mit der Handy-Kamera scannen — <b>kein Login nötig</b>. {isPapers
                        ? <>Fahrzeugschein (ZB1) fotografieren; die Fahrzeugdaten erscheinen<b> automatisch hier</b> am PC.</>
                        : <>ZB1, Ausweis, Rechnung/Auftrag oder Schadensfotos aufnehmen; die Daten erscheinen<b> automatisch hier</b> am PC.</>}
                </p>
                <button type="button" onClick={handlePrint} disabled={!scanUrl}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black disabled:opacity-50"
                    data-testid="wo-mobile-zb1-qr-print">
                    <Printer className="w-4 h-4" /> 24/7 Aushang drucken (A4)
                </button>

                {/* Standort-Bindung — Aufträge gehen an genau diesen Standort */}
                {session?.branch_label && (
                    <div className="w-full flex items-center gap-1.5 text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1" data-testid="wo-mobile-zb1-branch">
                        <MapPin className="w-3 h-3 text-emerald-600 shrink-0" />
                        <span>Aufträge gehen an: <b className="text-gray-700">{session.branch_label}</b></span>
                    </div>
                )}

                {/* Link an den Kunden senden (Remote-Selbstannahme) */}
                <div className="w-full border-t border-gray-100 pt-2 mt-1 space-y-2 text-left">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Link an Kunden senden</div>
                    <div className="relative">
                        <Input value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Kunde suchen (füllt Tel./E-Mail)…"
                            className="h-8 text-xs rounded-sm" data-testid="wo-mobile-zb1-send-customer" />
                        {custResults.length > 0 && (
                            <div className="absolute z-10 left-0 right-0 mt-1 border border-gray-200 bg-white rounded-sm max-h-40 overflow-y-auto divide-y divide-gray-100 shadow-md" data-testid="wo-mobile-zb1-send-customer-results">
                                {custResults.map((c) => (
                                    <button key={c.id} type="button" onClick={() => pickCustomer(c)}
                                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50"
                                        data-testid={`wo-mobile-zb1-send-customer-${c.id}`}>
                                        <span className="font-medium">{c.type === "business" && c.company_name ? c.company_name : `${c.first_name || ""} ${c.last_name || ""}`.trim()}</span>
                                        {c.phone ? <span className="text-gray-400"> · {c.phone}</span> : null}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-1.5">
                        <Input value={sendPhone} onChange={(e) => setSendPhone(e.target.value)} placeholder="Handynummer"
                            className="h-8 text-xs rounded-sm" data-testid="wo-mobile-zb1-send-phone" />
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        <button type="button" onClick={sendWhatsApp} disabled={!scanUrl}
                            className="inline-flex items-center justify-center gap-1 bg-[#25D366] text-white rounded-sm py-1.5 text-[11px] font-bold hover:brightness-95 disabled:opacity-50"
                            data-testid="wo-mobile-zb1-send-whatsapp">
                            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                        <button type="button" onClick={() => sendVia("sms")} disabled={!scanUrl || sending === "sms"}
                            className="inline-flex items-center justify-center gap-1 bg-gray-700 text-white rounded-sm py-1.5 text-[11px] font-bold hover:bg-gray-800 disabled:opacity-50"
                            data-testid="wo-mobile-zb1-send-sms">
                            {sending === "sms" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} SMS
                        </button>
                    </div>
                    <div className="flex gap-1.5">
                        <Input value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} type="email" placeholder="E-Mail"
                            className="h-8 text-xs rounded-sm" data-testid="wo-mobile-zb1-send-email-input" />
                        <button type="button" onClick={() => sendVia("email")} disabled={!scanUrl || sending === "email"}
                            className="shrink-0 inline-flex items-center justify-center gap-1 bg-[#111827] text-white rounded-sm px-3 text-[11px] font-bold hover:bg-black disabled:opacity-50"
                            data-testid="wo-mobile-zb1-send-email">
                            {sending === "email" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Senden
                        </button>
                    </div>
                </div>
            </div>
        </PopoverContent>
    );

    // ---- Header-Variante: größerer, direkt scannbarer QR oben rechts ----------
    if (variant === "header") {
        return (
            <div className="flex flex-col items-center gap-1.5" data-testid="wo-mobile-zb1-qr">
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            title="Mit dem Handy scannen → Belege am Smartphone aufnehmen (Klick: vergrößern & drucken)"
                            className="bg-white rounded-md p-1.5 hover:ring-2 hover:ring-[#FFCC00] transition-shadow shadow-lg"
                            data-testid="wo-mobile-zb1-qr-trigger"
                        >
                            {scanned ? (
                                <div className="w-[72px] h-[72px] flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                                </div>
                            ) : scanUrl ? (
                                <QRCodeSVG value={scanUrl} size={72} level="M" title="QR-Code zur mobilen Auftragserfassung" />
                            ) : (
                                <div className="w-[72px] h-[72px] flex items-center justify-center"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>
                            )}
                        </button>
                    </PopoverTrigger>
                    {popover}
                </Popover>
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1 max-w-[110px] text-center leading-tight">
                    <Smartphone className="w-3 h-3 shrink-0" /> 24/7 CHECK-IN Selbst-Annahme
                </span>
            </div>
        );
    }

    // ---- Inline-Variante (klein) ---------------------------------------------
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title="Mit dem Handy scannen → Belege am Smartphone (Ergebnis erscheint hier automatisch)"
                    className="relative flex flex-col items-center justify-center shrink-0 bg-white border border-gray-300 rounded-sm px-2 py-1 hover:border-[#FFCC00] hover:shadow-sm transition-colors"
                    data-testid="wo-mobile-zb1-qr"
                >
                    {scanned ? (
                        <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                    ) : scanUrl ? (
                        <QRCodeSVG value={scanUrl} size={40} level="M" title="QR-Code zur mobilen Auftragserfassung" />
                    ) : (
                        <Loader2 className="w-8 h-8 text-gray-300 animate-spin" />
                    )}
                    <span className="text-[8px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-0.5 mt-0.5">
                        <Smartphone className="w-2.5 h-2.5" /> 24/7 Check-in
                    </span>
                </button>
            </PopoverTrigger>
            {popover}
        </Popover>
    );
}
